import getErrorDetail from '@jetshop/core/helpers/getErrorDetail';
import { useMachine } from '@xstate/react';
import { useCallback, useMemo } from 'react';
import { assign, Machine } from 'xstate';
const defaultContext = {
    error: null,
    fields: {}
};
const enabled = (context) => context.enabled;
const setError = assign({
    error: (_, event) => event.data
});
const interpretError = assign({
    error: context => getErrorDetail(context.error).codes
});
const unsetError = assign({ error: null });
const unsetFields = assign({ fields: {} });
const onChange = assign({
    fields: (context, event) => (Object.assign(Object.assign({}, context.fields), { [event.key]: event.value }))
});
const SubscriptionMachine = Machine({
    id: 'SubscriptionMachine',
    initial: 'inactive',
    context: {
        error: null,
        fields: {}
    },
    states: {
        inactive: {
            always: {
                cond: 'enabled',
                target: 'active.idle'
            },
            on: {
                ACTIVATE: 'active.idle'
            }
        },
        active: {
            initial: 'idle',
            states: {
                idle: {
                    on: {
                        ONFOCUS: 'editing',
                        TRY_SUBMIT: 'submitting'
                    }
                },
                editing: {
                    onEntry: 'unsetError',
                    on: {
                        TRY_SUBMIT: 'submitting',
                        ONBLUR: 'idle',
                        ONCHANGE: {
                            actions: 'onChange'
                        }
                    }
                },
                submitting: {
                    invoke: {
                        id: 'submitting',
                        src: 'trySubmit',
                        onDone: '#SubscriptionMachine.submitted',
                        onError: {
                            actions: 'setError',
                            target: '#SubscriptionMachine.failed'
                        }
                    }
                }
            }
        },
        submitted: {
            onEntry: 'unsetFields',
            on: {
                ONFOCUS: '#SubscriptionMachine.active.editing'
            },
            after: {
                5000: {
                    target: 'inactive'
                }
            }
        },
        failed: {
            onEntry: 'interpretError',
            on: {
                ONBLUR: '#SubscriptionMachine.active.idle',
                ONFOCUS: '#SubscriptionMachine.active.editing',
                ONCHANGE: {
                    actions: 'onChange',
                    target: '#SubscriptionMachine.active.editing'
                }
            }
        }
    },
    on: {
        INACTIVATE: '#SubscriptionMachine.inactive'
    }
}, {
    actions: {
        setError,
        unsetError,
        interpretError,
        unsetFields,
        onChange
    },
    guards: {
        enabled
    }
});
function useStoreSubscriptions({ services, enabled }) {
    const [current, send] = useMachine(SubscriptionMachine, {
        services: Object.assign({}, services),
        context: Object.assign(Object.assign({}, defaultContext), { enabled })
    });
    const onFocus = useCallback(() => send('ONFOCUS'), [send]);
    const onBlur = useCallback(() => send('ONBLUR'), [send]);
    const onChange = useCallback((event) => send({
        type: 'ONCHANGE',
        key: event.target.name,
        value: event.target.value
    }), [send]);
    const submit = useCallback(e => {
        e.preventDefault();
        send('TRY_SUBMIT');
    }, [send]);
    const { fields, error } = current.context;
    const states = useMemo(() => ({
        inactive: current.matches('inactive'),
        active: current.matches('active'),
        idle: current.matches('active.idle'),
        editing: current.matches('active.editing'),
        submitting: current.matches('active.submitting'),
        submitted: current.matches('submitted'),
        failed: current.matches('failed')
    }), [current]);
    const { inactive } = states;
    const toggle = useCallback(() => send(inactive ? 'ACTIVATE' : 'INACTIVATE'), [
        inactive,
        send
    ]);
    const input = {
        onFocus,
        onBlur,
        onChange
    };
    return Object.assign(Object.assign({}, states), { error,
        input,
        fields,
        toggle,
        submit });
}
export { useStoreSubscriptions };
//# sourceMappingURL=useStoreSubscriptions.js.map